import { Component, EventEmitter, Input, Output, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { select, Store } from '@ngrx/store';
import * as Constants from '@emi/shared/constants';
import { ActionBarRoute } from './action-bar-route.model';
import { MatDialog } from '@angular/material/dialog';
import { GenericModalComponent } from 'libs/shared/ui/src/lib/generic-modal/generic-modal.component';
import { ProgressBarHandler } from '../progress-bar/progress-bar.handler';
import { DataExtractionState, getCountries, getSelectedFilters } from '@emi/shared/state/filters';
import { selectSkuSearch, SkuSearchState } from '@emi/shared/state/sku-search';
import { SkuSearchService as GetSkus } from '@emi/shared/data-access';

@Component({
    selector: 'via-action-bar',
    templateUrl: './action-bar.component.html',
    styleUrls: ['./action-bar.component.scss'],
})
export class ActionBarComponent implements OnInit {
    @Input()
    public isSkuSearch = false;

    @Input()
    public showCancelButton = false;

    @Input()
    public showApplyButton = false;

    @Input()
    public showBackButton = true;

    @Input()
    public showCompleteButton = false;

    @Input()
    public disableApplyButton = false;

    @Input()
    public isNextButtonHidden = false;

    @Input()
    public isCompleteButtonDisabled: boolean;

    @Input()
    public hasPreviousSelections: boolean;

    @Input()
    public applyButtonText = 'Apply';

    @Input()
    public nextButtonText = 'Next';

    @Output()
    public onApplyClicked = new EventEmitter();

    @Output()
    public onCancelClicked = new EventEmitter();

    public currentPageRoute: ActionBarRoute;
    public selectionsMade = false;
    public searchTerm: string;
    public modalType = Constants.LocalStorageValues.REMOVE_SELECTION_MODAL;
    public nextButtonIconHtml = '<i class="fa-regular fa-arrow-right"></i>';
    public isSearchRoute = this.router.url.includes(Constants.Routes.SKU_SEARCH);

    public routes: Array<ActionBarRoute> = [
        {
            route: this.isSearchRoute ? Constants.Routes.SKU_SEARCH_RESULTS : Constants.Routes.FILTER_DATA_ROUTE,
            pageNumber: 1,
        },
        {
            route: this.isSearchRoute ? Constants.Routes.SKU_SEARCH_CONFIGURE_DATA_ROUTE : Constants.Routes.CONFIGURE_DATA_ROUTE,
            pageNumber: 2,
        },
        {
            route: this.isSearchRoute ? Constants.Routes.SKU_SEARCH_SAVE_AND_EXTRACT_ROUTE : Constants.Routes.SAVE_AND_EXTRACT_ROUTE,
            pageNumber: 3,
        },
    ];

    constructor(
        private router: Router,
        private readonly store: Store<DataExtractionState>,
        private readonly storeSkuSearchState: Store<SkuSearchState>,
        private progressBarHandler: ProgressBarHandler,
        public dialog: MatDialog,
        private getSkusService: GetSkus
    ) {}

    public ngOnInit(): void {
        this.store.pipe(select(getSelectedFilters)).subscribe((x) => {
            this.selectionsMade = x.countries && x.countries.length > 0 && x.categories && x.categories.length > 0;
        });

        this.storeSkuSearchState.pipe(select(selectSkuSearch)).subscribe((x) => {
            this.searchTerm = x.searchTerm;
        });

        this.currentPageRoute = this.routes.find((x) => this.router.url.includes(x.route))!;
    }

    public nextClicked(): void {
        if (this.currentPageRoute.pageNumber === this.routes.length) {
            this.onApplyClicked.emit();
        }
        if (this.currentPageRoute && this.currentPageRoute.pageNumber < this.routes.length) {
            if (!this.isSkuSearch) {
                this.progressBarHandler.highlightNextStep(false);
            }

            const nextPageRoute = this.routes.find((x) => x.pageNumber === this.currentPageRoute.pageNumber + 1)!;
            this.router.navigate([nextPageRoute.route]);
        }
    }

    public cancelClicked(): void {
        this.onCancelClicked.emit();
    }

    public backClicked(): void {
        if (this.currentPageRoute && this.currentPageRoute.pageNumber > 1) {
            const prevPageRoute = this.routes.find((x) => x.pageNumber === this.currentPageRoute.pageNumber - 1)!;
            this.router.navigate([prevPageRoute.route]);
            this.getSkusService.updateSearchFilterText(false);
            this.store.dispatch(getCountries());
        }
    }

    public applyClicked(): void {
        if (this.hasPreviousSelections) {
            const show = localStorage.getItem(this.modalType) ?? 'true';
            const showDeleteModal = JSON.parse(show);
            if (showDeleteModal) {
                const dialogRef = this.dialog.open(GenericModalComponent, {
                    width: '340px',
                    data: {
                        buttons: {
                            primary: 'Proceed',
                            secondary: 'Cancel',
                        },
                        content:
                            'You are removing a filter selection which will make other filters and/or selections irrelevant. Do you want to continue?',
                        iconClass: 'far fa-filter-circle-xmark',
                        title: 'Selection Removal',
                        type: this.modalType,
                        showUnderstoodCheckbox: true,
                    },
                });

                dialogRef.afterClosed().subscribe((result) => {
                    if (result) {
                        this.onApplyClicked.emit();
                    }
                });
            } else {
                this.onApplyClicked.emit();
            }
        } else {
            this.onApplyClicked.emit();
        }
    }

    public nextButtonDisabled(): boolean {
        if (this.isCompleteButtonDisabled) {
            return true;
        }
        if (this.isNextButtonHidden) {
            return true;
        }
        if (this.searchTerm && this.searchTerm.length > 0) {
            return false;
        } else {
            return !this.selectionsMade;
        }
    }

    public nextHover(event: MouseEvent): void {
        if (this.isSkuSearch) {
            return;
        }
        if (event.type === 'mouseenter' && !this.nextButtonDisabled()) {
            this.progressBarHandler.highlightNextStep(true);
        } else {
            this.progressBarHandler.highlightNextStep(false);
        }
    }
}
